import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { GivelifyLabel } from '../../label';
import { GivelifyLoading } from '../../loading';
import { DesignTokens } from '../../specify';
import { useImageEditorContext } from '../context/ImageEditorContext';
import {
    CropWrapper,
    FileNameContainer,
    LoadingContainer,
    StyledNotification,
    UploadProgress,
    UploadProgressBar,
    UploadProgressFill,
    UploadProgressRow,
    Wrapper,
} from '../style';
import { ControlsPanel } from './ControlsPanel';
import { EditorCanvas } from './EditorCanvas';
import { EditorError } from './EditorError';

export const EditorContent: React.FC = () => {
    const {
        copy,
        aspectRatio,
        avatar,
        isBusy,
        isUploading,
        uploadProgress,
        uploadError,
        fileName,
        processingLabel,
        containerRef,
        fitSizes,
        imageData,
        isRotating,
        offset,
        rotation,
        zoom,
        backgroundSelection,
        backgroundOptions,
        hideBackgroundSelection,
        isBackgroundVisible,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onBackgroundChange,
        onRotate,
        onZoomChange,
        onZoomMinus,
        onZoomPlus,
        sliderValue,
    } = useImageEditorContext();

    const [name, ext] = useMemo(() => {
        const parts = fileName.split('.');
        const ext = parts.pop();
        return [parts.join(''), `.${ext}`];
    }, [fileName]);

    return (
        <Wrapper
            container
            direction="column"
            flexWrap="nowrap"
            isUploading={isUploading}
        >
            <Grid item className="loading-container">
                {isBusy ? (
                    <LoadingContainer>
                        <FileNameContainer>
                            <GivelifyLabel
                                className="file-name truncate"
                                text={name}
                                variant="body1"
                            />
                            <GivelifyLabel
                                className="file-name"
                                text={ext}
                                variant="body1"
                            />
                        </FileNameContainer>
                        <Grid
                            container
                            alignItems="center"
                            gap="8px"
                            justifyContent="center"
                        >
                            <GivelifyLoading
                                color="inherit"
                                size={16}
                                type="circular"
                            />
                            <GivelifyLabel
                                color={DesignTokens.color.textSecondary}
                                text={processingLabel}
                            />
                        </Grid>
                    </LoadingContainer>
                ) : (
                    <CropWrapper>
                        {uploadError ? (
                            <StyledNotification
                                open
                                ClickAwayListenerProps={{
                                    onClickAway: () => undefined,
                                }}
                                customMessage={
                                    <EditorError
                                        description={uploadError.description}
                                        onDismiss={uploadError.onDismiss}
                                        onSupport={uploadError?.onSupport}
                                        showContactSupport={
                                            !!uploadError.onSupport
                                        }
                                        title={uploadError.text}
                                    />
                                }
                                variant="error"
                            />
                        ) : null}
                        <EditorCanvas
                            aspectRatio={aspectRatio}
                            avatar={avatar}
                            backgroundSelection={backgroundSelection}
                            containerRef={containerRef}
                            fitSizes={fitSizes}
                            imageData={imageData}
                            isRotating={isRotating}
                            offset={offset}
                            onPointerDown={onPointerDown}
                            onPointerMove={onPointerMove}
                            onPointerUp={onPointerUp}
                            rotation={rotation}
                            showGrid={!isUploading}
                            zoom={zoom}
                        />
                        {isUploading ? (
                            <UploadProgress>
                                <UploadProgressBar>
                                    <UploadProgressFill
                                        progress={uploadProgress}
                                    />
                                </UploadProgressBar>
                                <UploadProgressRow>
                                    <GivelifyLabel
                                        text={copy.saving}
                                        variant="body2"
                                    />
                                    <GivelifyLabel
                                        text={`${uploadProgress}%`}
                                        variant="body2"
                                    />
                                </UploadProgressRow>
                            </UploadProgress>
                        ) : (
                            <ControlsPanel
                                backgroundOptions={backgroundOptions}
                                backgroundSelection={backgroundSelection}
                                hideBackgroundSelection={
                                    hideBackgroundSelection
                                }
                                imageData={imageData}
                                isBackgroundVisible={isBackgroundVisible}
                                onBackgroundChange={onBackgroundChange}
                                onRotate={onRotate}
                                onZoomChange={onZoomChange}
                                onZoomMinus={onZoomMinus}
                                onZoomPlus={onZoomPlus}
                                sliderValue={sliderValue}
                            />
                        )}
                    </CropWrapper>
                )}
            </Grid>
        </Wrapper>
    );
};
